/**
 * Bitcoin Retirement Calculator Service
 * Based on https://github.com/pampeanodev/btcretirementcalc
 */

import {
  BitcoinRetirementInput,
  BitcoinRetirementResult,
  AnnualBitcoinPrice,
  AnnualTrackingData,
  RetirementSummary,
} from '../types/bitcoinRetirement.types';

// Default Bitcoin price (used as starting point)
const DEFAULT_BTC_PRICE = 100000;

/**
 * Convert annual percentage to growth factor
 * e.g., 20% -> 1.20
 */
function getGrowthFactor(annualPercentage: number): number {
  return 1 + annualPercentage / 100;
}

/**
 * Convert inflation rate to inflation factor
 * e.g., 2% -> 1.02
 */
function getInflationFactor(inflationRate: number): number {
  return 1 + inflationRate / 100;
}

/**
 * Calculate Bitcoin price history with indexed values
 */
function calculateBitcoinPriceHistory(
  input: BitcoinRetirementInput,
  currentBtcPrice: number
): AnnualBitcoinPrice[] {
  const growthFactor = getGrowthFactor(input.bitcoinCAGR);
  const inflationFactor = getInflationFactor(input.inflationRate);
  const currentYear = new Date().getFullYear();

  const priceHistory: AnnualBitcoinPrice[] = [];
  let bitcoinPrice = currentBtcPrice;
  let annualBudget = input.desiredRetirementIncome;

  for (let age = input.currentAge; age <= input.lifeExpectancy; age++) {
    const year = currentYear + (age - input.currentAge);

    priceHistory.push({
      year,
      age,
      bitcoinPriceIndexed: bitcoinPrice,
      desiredAnnualBudgetIndexed: annualBudget,
    });

    // Apply growth and inflation for next year
    bitcoinPrice *= growthFactor;
    annualBudget *= inflationFactor;
  }

  return priceHistory;
}

/**
 * Calculate total fiat needed from a given age until life expectancy
 */
function calculateFiatNeededOverLife(
  priceHistory: AnnualBitcoinPrice[],
  fromAge: number
): number {
  return priceHistory
    .filter((item) => item.age >= fromAge)
    .reduce((sum, item) => sum + item.desiredAnnualBudgetIndexed, 0);
}

/**
 * Calculate total bitcoin needed from a given age until life expectancy
 */
function calculateBitcoinNeededOverLife(
  priceHistory: AnnualBitcoinPrice[],
  fromAge: number
): number {
  return priceHistory
    .filter((item) => item.age >= fromAge)
    .reduce(
      (sum, item) => sum + item.desiredAnnualBudgetIndexed / item.bitcoinPriceIndexed,
      0
    );
}

/**
 * Standard calculation - determines retirement age based on accumulated savings
 */
function buildRetirementPrediction(
  input: BitcoinRetirementInput,
  priceHistory: AnnualBitcoinPrice[],
  inflationFactor: number
): BitcoinRetirementResult {
  const dataSet: AnnualTrackingData[] = [];
  let accumulatedSavingsBitcoin = input.currentSavingsInBitcoin;
  let indexedAnnualBuyInFiat = input.annualBuyInFiat;
  let retirementAge = -1;
  let canRetire = false;
  let bitcoinPriceAtRetirement = 0;
  let annualRetirementBudget = 0;
  let annualRetirementBudgetAtRetirementAge = 0;

  for (let i = 0; i < priceHistory.length; i++) {
    const priceData = priceHistory[i];
    const { year, age, bitcoinPriceIndexed, desiredAnnualBudgetIndexed } = priceData;

    // Calculate current fiat value
    const accumulatedSavingsFiat = accumulatedSavingsBitcoin * bitcoinPriceIndexed;

    // Check if can retire (not yet retired)
    if (retirementAge === -1) {
      const pendingFiatNeeded = calculateFiatNeededOverLife(priceHistory, age);

      if (accumulatedSavingsFiat >= pendingFiatNeeded) {
        retirementAge = age;
        canRetire = true;
        bitcoinPriceAtRetirement = bitcoinPriceIndexed;
        annualRetirementBudgetAtRetirementAge = desiredAnnualBudgetIndexed;

        // Calculate sustainable annual budget
        const yearsInRetirement = input.lifeExpectancy - age + 1;
        annualRetirementBudget = accumulatedSavingsFiat / yearsInRetirement;
      }
    }

    // Track bitcoin flow
    let bitcoinFlow = 0;
    if (retirementAge === -1 || age < retirementAge) {
      // Still accumulating
      bitcoinFlow = indexedAnnualBuyInFiat / bitcoinPriceIndexed;
      accumulatedSavingsBitcoin += bitcoinFlow;
    } else {
      // In retirement - spending
      const btcToSpend = desiredAnnualBudgetIndexed / bitcoinPriceIndexed;
      bitcoinFlow = -btcToSpend;
      accumulatedSavingsBitcoin -= btcToSpend;
    }

    dataSet.push({
      key: i,
      year,
      age,
      savingsBitcoin: accumulatedSavingsBitcoin,
      savingsFiat: accumulatedSavingsBitcoin * bitcoinPriceIndexed,
      bitcoinFlow,
      bitcoinPrice: bitcoinPriceIndexed,
      annualRetirementBudget: retirementAge !== -1 ? desiredAnnualBudgetIndexed : 0,
      phase: retirementAge === -1 || age < retirementAge ? 'accumulation' : 'retirement',
    });

    // Apply inflation for next year's contributions
    indexedAnnualBuyInFiat *= inflationFactor;
  }

  // If couldn't retire, set to life expectancy
  if (retirementAge === -1) {
    retirementAge = input.lifeExpectancy;
    const lastData = dataSet[dataSet.length - 1];
    bitcoinPriceAtRetirement = lastData?.bitcoinPrice || DEFAULT_BTC_PRICE;
  }

  const totalContributions =
    input.currentSavingsInBitcoin * priceHistory[0].bitcoinPriceIndexed +
    input.annualBuyInFiat * (retirementAge - input.currentAge);

  return {
    canRetire,
    retirementAge,
    savingsBitcoin: dataSet.find((d) => d.age === retirementAge)?.savingsBitcoin || 0,
    savingsFiat: dataSet.find((d) => d.age === retirementAge)?.savingsFiat || 0,
    bitcoinPriceAtRetirementAge: bitcoinPriceAtRetirement,
    annualRetirementBudget,
    annualRetirementBudgetAtRetirementAge,
    startingBitcoinPrice: priceHistory[0].bitcoinPriceIndexed,
    dataSet,
    optimized: false,
    totalBitcoinAccumulated:
      dataSet.find((d) => d.age === retirementAge)?.savingsBitcoin || 0,
    yearsUntilRetirement: Math.max(0, retirementAge - input.currentAge),
    totalContributions,
  };
}

/**
 * Optimized calculation - finds optimal retirement strategy
 * Distributes Bitcoin evenly over retirement years instead of selling based on fiat needs
 */
function buildOptimizedRetirementPrediction(
  input: BitcoinRetirementInput,
  priceHistory: AnnualBitcoinPrice[],
  inflationFactor: number
): BitcoinRetirementResult {
  const dataSet: AnnualTrackingData[] = [];
  let accumulatedSavingsBitcoin = input.currentSavingsInBitcoin;
  let indexedAnnualBuyInFiat = input.annualBuyInFiat;
  let retirementAge = -1;
  let canRetire = false;
  let bitcoinPriceAtRetirement = 0;
  let annualRetirementBudgetAtRetirementAge = 0;
  let annualRetirementBudget = 0;
  let bitcoinPerYear = 0;

  for (let i = 0; i < priceHistory.length; i++) {
    const priceData = priceHistory[i];
    const { year, age, bitcoinPriceIndexed, desiredAnnualBudgetIndexed } = priceData;

    // Calculate current fiat value
    const accumulatedSavingsFiat = accumulatedSavingsBitcoin * bitcoinPriceIndexed;

    // Check if can retire (not yet retired)
    if (retirementAge === -1) {
      const pendingBitcoinNeeded = calculateBitcoinNeededOverLife(priceHistory, age);

      if (accumulatedSavingsBitcoin >= pendingBitcoinNeeded) {
        retirementAge = age;
        canRetire = true;
        bitcoinPriceAtRetirement = bitcoinPriceIndexed;
        annualRetirementBudgetAtRetirementAge = desiredAnnualBudgetIndexed;

        // Distribute bitcoin evenly over retirement years
        const yearsInRetirement = input.lifeExpectancy - age + 1;
        bitcoinPerYear = accumulatedSavingsBitcoin / yearsInRetirement;
        annualRetirementBudget = bitcoinPerYear * bitcoinPriceIndexed;
      }
    }

    // Track bitcoin flow
    let bitcoinFlow = 0;
    let currentAnnualBudget = 0;

    if (retirementAge === -1 || age < retirementAge) {
      // Still accumulating
      bitcoinFlow = indexedAnnualBuyInFiat / bitcoinPriceIndexed;
      accumulatedSavingsBitcoin += bitcoinFlow;
    } else {
      // In retirement - sell fixed bitcoin amount
      bitcoinFlow = -bitcoinPerYear;
      accumulatedSavingsBitcoin -= bitcoinPerYear;
      currentAnnualBudget = bitcoinPerYear * bitcoinPriceIndexed;
    }

    dataSet.push({
      key: i,
      year,
      age,
      savingsBitcoin: Math.max(0, accumulatedSavingsBitcoin),
      savingsFiat: Math.max(0, accumulatedSavingsBitcoin) * bitcoinPriceIndexed,
      bitcoinFlow,
      bitcoinPrice: bitcoinPriceIndexed,
      annualRetirementBudget: currentAnnualBudget,
      phase: retirementAge === -1 || age < retirementAge ? 'accumulation' : 'retirement',
    });

    // Apply inflation for next year's contributions
    indexedAnnualBuyInFiat *= inflationFactor;
  }

  // If couldn't retire, set to life expectancy
  if (retirementAge === -1) {
    retirementAge = input.lifeExpectancy;
    const lastData = dataSet[dataSet.length - 1];
    bitcoinPriceAtRetirement = lastData?.bitcoinPrice || DEFAULT_BTC_PRICE;
  }

  const totalContributions =
    input.currentSavingsInBitcoin * priceHistory[0].bitcoinPriceIndexed +
    input.annualBuyInFiat * (retirementAge - input.currentAge);

  return {
    canRetire,
    retirementAge,
    savingsBitcoin: dataSet.find((d) => d.age === retirementAge)?.savingsBitcoin || 0,
    savingsFiat: dataSet.find((d) => d.age === retirementAge)?.savingsFiat || 0,
    bitcoinPriceAtRetirementAge: bitcoinPriceAtRetirement,
    annualRetirementBudget,
    annualRetirementBudgetAtRetirementAge,
    startingBitcoinPrice: priceHistory[0].bitcoinPriceIndexed,
    dataSet,
    optimized: true,
    totalBitcoinAccumulated:
      dataSet.find((d) => d.age === retirementAge)?.savingsBitcoin || 0,
    yearsUntilRetirement: Math.max(0, retirementAge - input.currentAge),
    totalContributions,
  };
}

/**
 * Main calculation function
 */
export function calculateBitcoinRetirement(
  input: BitcoinRetirementInput,
  currentBtcPrice: number = DEFAULT_BTC_PRICE
): BitcoinRetirementResult {
  const inflationFactor = getInflationFactor(input.inflationRate);
  const priceHistory = calculateBitcoinPriceHistory(input, currentBtcPrice);

  if (input.optimized) {
    return buildOptimizedRetirementPrediction(input, priceHistory, inflationFactor);
  }

  return buildRetirementPrediction(input, priceHistory, inflationFactor);
}

/**
 * Get retirement summary from result
 */
export function getRetirementSummary(
  result: BitcoinRetirementResult,
  input: BitcoinRetirementInput
): RetirementSummary {
  const retirementData = result.dataSet.find((d) => d.age === result.retirementAge);

  return {
    retirementAge: result.retirementAge,
    yearsUntilRetirement: result.yearsUntilRetirement,
    totalBitcoinAtRetirement: retirementData?.savingsBitcoin || 0,
    portfolioValueAtRetirement: retirementData?.savingsFiat || 0,
    monthlyRetirementIncome: result.annualRetirementBudget / 12,
    btcPriceAtRetirement: result.bitcoinPriceAtRetirementAge,
    totalContributions: result.totalContributions,
    roi:
      result.totalContributions > 0
        ? ((retirementData?.savingsFiat || 0) / result.totalContributions - 1) * 100
        : 0,
  };
}

/**
 * Format currency for display
 */
export function formatCurrency(value: number, decimals: number = 0): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format Bitcoin amount
 */
export function formatBitcoin(value: number): string {
  if (value >= 1) {
    return `₿${value.toFixed(4)}`;
  }
  return `₿${value.toFixed(8)}`;
}
